import { useState } from "react";
import { Trophy } from "lucide-react";
import type { PipelineStage } from "@school-sales-ai/shared";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { PIPELINE_COLOR_OPTIONS } from "@/lib/stage-colors";
import {
  useCreatePipelineStage,
  useDeletePipelineStage,
  usePipelineStagesQuery,
  useReorderPipelineStages,
  useUpdatePipelineStage,
} from "@/hooks/usePipelineStages";

type Props = {
  open: boolean;
  onClose: () => void;
  // Active lead count per column key — used to block deleting a non-empty column.
  leadCounts: Record<string, number>;
};

export function ManageColumnsModal({
  open,
  onClose,
  leadCounts,
}: Props): React.JSX.Element {
  const { data: stages } = usePipelineStagesQuery();
  const createStage = useCreatePipelineStage();
  const updateStage = useUpdatePipelineStage();
  const deleteStage = useDeletePipelineStage();
  const reorderStages = useReorderPipelineStages();

  const [confirmingDeleteId, setConfirmingDeleteId] = useState<string | null>(null);

  const ordered = stages ?? [];
  const nonFixed = ordered.filter((s) => !s.isFixed);

  function handleRename(stage: PipelineStage, value: string): void {
    const label = value.trim();
    if (!label || label === stage.label) return;
    updateStage.mutate({ id: stage.id, data: { label } });
  }

  function handleReorder(stageId: string, direction: -1 | 1): void {
    const idx = nonFixed.findIndex((s) => s.id === stageId);
    const target = idx + direction;
    if (idx < 0 || target < 0 || target >= nonFixed.length) return;
    const ids = nonFixed.map((s) => s.id);
    [ids[idx], ids[target]] = [ids[target]!, ids[idx]!];
    reorderStages.mutate(ids);
  }

  function handleDelete(stageId: string): void {
    deleteStage.mutate(stageId, { onSuccess: () => setConfirmingDeleteId(null) });
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="flex max-h-[85vh] flex-col sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Administrar columnas</DialogTitle>
          <DialogDescription>
            Renombra, colorea, reordena o elimina columnas. Las columnas fijas no
            se pueden modificar. Solo puedes eliminar columnas vacías.
            <br />
            La columna marcada como <strong>cierre ganado</strong> es la que miden
            los reportes y el resumen diario. Las marcadas como fuera del embudo
            dejan de aparecer en pendientes y en el correo del día.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-2 overflow-y-auto pr-1">
          {ordered.map((stage) => {
            const count = leadCounts[stage.key] ?? 0;
            const isNonFixed = !stage.isFixed;
            const isConversion = stage.stageRole === "conversion";
            const position = nonFixed.findIndex((s) => s.id === stage.id);

            return (
              <div
                key={stage.id}
                className="rounded-[12px] border border-ink-100 bg-white p-3 flex flex-col gap-2"
              >
                <div className="flex items-center gap-2">
                  {stage.isFixed ? (
                    <>
                      <span className="flex-1 text-sm font-medium text-ink-800">
                        {stage.label}
                      </span>
                      <span className="rounded-full bg-ink-100 px-2 py-0.5 text-[10px] font-semibold text-ink-500">
                        Fija
                      </span>
                    </>
                  ) : (
                    <>
                      <input
                        key={stage.id}
                        type="text"
                        defaultValue={stage.label}
                        maxLength={64}
                        onBlur={(e) => handleRename(stage, e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") e.currentTarget.blur();
                        }}
                        className="flex-1 rounded-[8px] border border-ink-200 px-2 py-1 text-sm text-ink-800 focus:border-moss focus:outline-none"
                      />
                      <div className="flex items-center gap-0.5">
                        <button
                          type="button"
                          aria-label="Subir"
                          disabled={position <= 0 || reorderStages.isPending}
                          onClick={() => handleReorder(stage.id, -1)}
                          className="rounded p-1 text-ink-500 hover:bg-ink-050 disabled:opacity-30"
                        >
                          ▲
                        </button>
                        <button
                          type="button"
                          aria-label="Bajar"
                          disabled={position >= nonFixed.length - 1 || reorderStages.isPending}
                          onClick={() => handleReorder(stage.id, 1)}
                          className="rounded p-1 text-ink-500 hover:bg-ink-050 disabled:opacity-30"
                        >
                          ▼
                        </button>
                      </div>
                    </>
                  )}
                </div>

                {isNonFixed && (
                  <div className="flex flex-wrap items-center gap-3">
                    {/* Color picker */}
                    <div className="flex items-center gap-1">
                      {PIPELINE_COLOR_OPTIONS.map((c) => (
                        <button
                          key={c.name}
                          type="button"
                          aria-label={`Color ${c.name}`}
                          onClick={() =>
                            updateStage.mutate({ id: stage.id, data: { color: c.name } })
                          }
                          className={`h-5 w-5 rounded-full ${c.swatch} ${
                            stage.color === c.name
                              ? "ring-2 ring-ink-900 ring-offset-1"
                              : ""
                          }`}
                        />
                      ))}
                    </div>

                    {/* Conversion column. A radio and not a switch on purpose:
                        there is no "none" option, so the UI cannot offer the state
                        that leaves every conversion metric reading zero. Picking
                        one demotes the previous holder server-side. */}
                    <label className="flex items-center gap-1.5 text-xs text-ink-600">
                      <input
                        type="radio"
                        name="conversion-stage"
                        checked={isConversion}
                        onChange={() =>
                          updateStage.mutate({
                            id: stage.id,
                            data: { stageRole: "conversion" },
                          })
                        }
                        className="h-3.5 w-3.5 accent-moss"
                      />
                      <Trophy className="h-3.5 w-3.5 text-moss" aria-hidden />
                      Cierre ganado
                    </label>

                    {/* requiresReason toggle */}
                    <label className="flex items-center gap-1.5 text-xs text-ink-600">
                      <Switch
                        checked={stage.requiresReason}
                        onCheckedChange={(v) =>
                          updateStage.mutate({
                            id: stage.id,
                            data: { requiresReason: v },
                          })
                        }
                      />
                      Pedir razón al mover aquí
                    </label>

                    {/* Out of the funnel. Disabled on the conversion column — it is
                        already terminal, and the two roles are exclusive. */}
                    <label
                      className={`flex items-center gap-1.5 text-xs ${
                        isConversion ? "text-ink-300" : "text-ink-600"
                      }`}
                    >
                      <Switch
                        checked={stage.stageRole === "lost"}
                        disabled={isConversion}
                        onCheckedChange={(v) =>
                          updateStage.mutate({
                            id: stage.id,
                            data: { stageRole: v ? "lost" : null },
                          })
                        }
                      />
                      Fuera del embudo
                    </label>

                    {/* Delete */}
                    <div className="ml-auto flex items-center gap-2">
                      {confirmingDeleteId === stage.id ? (
                        <>
                          <span className="text-xs text-ink-600">¿Eliminar?</span>
                          <button
                            type="button"
                            disabled={deleteStage.isPending}
                            onClick={() => handleDelete(stage.id)}
                            className="rounded-lg bg-red-600 px-2 py-1 text-xs font-medium text-white hover:bg-red-700 disabled:opacity-40"
                          >
                            Sí
                          </button>
                          <button
                            type="button"
                            onClick={() => setConfirmingDeleteId(null)}
                            className="rounded-lg border border-ink-200 px-2 py-1 text-xs font-medium text-ink-700 hover:bg-ink-050"
                          >
                            No
                          </button>
                        </>
                      ) : (
                        <button
                          type="button"
                          disabled={count > 0 || isConversion}
                          title={
                            isConversion
                              ? "No se puede eliminar: es la columna de cierre ganado"
                              : count > 0
                                ? "No se puede eliminar: la columna tiene leads"
                                : "Eliminar columna"
                          }
                          onClick={() => setConfirmingDeleteId(stage.id)}
                          className="rounded-lg border border-red-200 px-2 py-1 text-xs font-medium text-red-600 hover:bg-red-50 disabled:cursor-not-allowed disabled:border-ink-100 disabled:text-ink-300"
                        >
                          Eliminar
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <button
          type="button"
          disabled={createStage.isPending}
          onClick={() => createStage.mutate({ label: "Nueva columna" })}
          className="mt-1 rounded-lg border border-dashed border-ink-300 py-2 text-sm font-medium text-ink-600 hover:bg-ink-050 disabled:opacity-40"
        >
          + Agregar columna
        </button>
      </DialogContent>
    </Dialog>
  );
}
